import React from 'react';
import { Box, Typography, Divider } from '@material-ui/core';

// Helper component for rendering structured help content sections
const HelpSection = ({ title, children }) => (
    <Box mb={3}>
        <Typography variant="subtitle1" color="primary" style={{ fontWeight: 700, marginBottom: '6px', textTransform: 'uppercase' }}>
            {title}
        </Typography>
        <Typography variant="body2" component="div" color="textSecondary" style={{ lineHeight: 1.6 }}>
            {children}
        </Typography>
    </Box>
);

const createStandardHelp = ({
    title,
    description,
    purpose,
    majorSections = [],
    commonWorkflows = "",
    businessRules = "",
    bestPractices = "",
}) => ({
    title,
    description,
    content: (
        <>
            <HelpSection title="Purpose of the screen">{purpose}</HelpSection>
            {majorSections.length > 0 && (
                <HelpSection title="Major Sections & Fields">
                    <ul>
                        {majorSections.map((item) => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                </HelpSection>
            )}
            {commonWorkflows ? (
                <HelpSection title="Common Workflows">{commonWorkflows}</HelpSection>
            ) : null}
            {businessRules ? (
                <HelpSection title="Business Rules & Warnings">{businessRules}</HelpSection>
            ) : null}
            {bestPractices ? (
                <HelpSection title="Best Practices">{bestPractices}</HelpSection>
            ) : null}
        </>
    ),
});

const additionalRouteHelps = [
    {
        path: "/admin/shopping-feed",
        exact: false,
        help: createStandardHelp({
            title: "Google Shopping Feed",
            description: "Generate and monitor merchant feed exports for Google Shopping.",
            purpose:
                "Configure the credentials and export settings used to deliver your catalog to Google Merchant Center or an FTP endpoint. This page is used when merchandising or marketing teams need a product feed outside the storefront itself.",
            majorSections: [
                "FTP credentials define where the generated feed file is sent.",
                "The sample file link shows the expected file structure before you generate a production export.",
                "The feed log lists generation attempts and any delivery errors returned by the destination server.",
            ],
            commonWorkflows:
                "Enter or verify the FTP destination, generate a feed, save the settings, then review the log to confirm the export completed successfully.",
            businessRules:
                "Incorrect credentials or malformed feed data will prevent Merchant Center from ingesting your products. Changes here affect scheduled feed generation for the selected store.",
            bestPractices:
                "Use the sample file to validate column mappings with marketing before turning on an automated feed. After changing credentials, run a manual generation and confirm the log shows success.",
        }),
    },
    {
        path: "/admin/shipping-import-rules",
        exact: false,
        help: createStandardHelp({
            title: "Shipping & Import Rules",
            description: "Define landed-cost rules, thresholds, and tax behavior by country and regime.",
            purpose:
                "Manage the rule engine used to calculate shipping, customs duties, VAT, and threshold logic for cross-border orders. These rules directly influence checkout totals and post-purchase margin accuracy.",
            majorSections: [
                "Country, regime, and status filters narrow the active rules list.",
                "Shipping Rates capture base, volumetric, minimum charge, and minimum-weight behavior.",
                "Limits and Tax Exemption define value, weight, quantity, and duty/VAT thresholds.",
                "Validity dates let you stage seasonal or temporary rule changes without deleting prior logic.",
            ],
            commonWorkflows:
                "Create a rule for a target country, validate rate thresholds, activate it, and then monitor resulting landed-cost behavior on test carts before enabling it broadly.",
            businessRules:
                "Bad numbers here will change customer-facing totals and margin calculations immediately. Overlapping active rules for the same country/regime should be avoided unless the effective dates are intentionally staged.",
            bestPractices:
                "Keep one clearly documented active rule per country/regime combination. When replacing a rule, prefer new validity dates over editing historical data so prior calculations remain explainable.",
        }),
    },
    {
        path: "/admin/reports/report",
        exact: false,
        help: createStandardHelp({
            title: "Sales Report",
            description: "Review order counts and amounts by status for a selected date range.",
            purpose:
                "Provide an operational summary of order volume and revenue grouped by status so teams can reconcile sales activity, detect bottlenecks, and export detailed CSV data for downstream analysis.",
            majorSections: [
                "From and To dates define the reporting window.",
                "The status table summarizes order count and total order amount per status.",
                "The Action column exports detailed order data for statuses that have volume in the selected period.",
            ],
            commonWorkflows:
                "Adjust the date range, review which statuses carry the most volume, and download the detailed CSV for the specific status that requires reconciliation or deeper analysis.",
            businessRules:
                "Large date ranges may take longer to load and export. The downloaded CSV reflects the filters on screen, so verify the date range before sharing the file externally.",
            bestPractices:
                "Use short windows for daily operational review and larger windows for finance reconciliation. When comparing periods, keep the same status mix and timezone assumptions.",
        }),
    },
    {
        path: "/admin/edit-store/:dealerId",
        exact: false,
        help: createStandardHelp({
            title: "Edit Store",
            description: "Maintain a single storefront across all permitted configuration tabs.",
            purpose:
                "Act as the consolidated editing workspace for one dealer/store. Each tab exposes a focused configuration area such as profile, pricing, shipping, redirects, or content, while keeping the operator scoped to the selected store.",
            majorSections: [
                "The tab bar is permission-aware and only shows sections the current user can edit.",
                "Each tab loads the underlying store configuration screen inside the same store context.",
                "Changes made inside a tab affect the currently selected dealer only, not the platform globally.",
            ],
            commonWorkflows:
                "Open the store, move through the tabs that need changes, save inside each section, and then verify the storefront behavior as that dealer if needed.",
            businessRules:
                "Because this screen reuses multiple store-specific modules, a change in one tab can affect assumptions in another. For example, shipping, tax, and payment settings should be reviewed together when onboarding a new store.",
            bestPractices:
                "Treat this page as a guided checklist for store setup. Complete identity, payment, tax, shipping, and content in a deliberate order rather than jumping randomly between tabs.",
        }),
    },
    {
        path: "/admin/timeline-page",
        exact: false,
        help: createStandardHelp({
            title: "Timeline",
            description: "Preview timeline-style UI patterns and chronological content blocks.",
            purpose:
                "A component showcase page used to validate and review timeline layouts, event ordering, and status visualization patterns in the design system.",
            commonWorkflows:
                "Use this page when evaluating how dated events, milestones, or audit-style items should appear before reusing the pattern elsewhere in the product.",
        }),
    },
    {
        path: "/admin/user-page",
        exact: false,
        help: createStandardHelp({
            title: "User Profile",
            description: "Review a profile-oriented page layout and user detail presentation.",
            purpose:
                "A reference page for user-centric layout patterns, combining profile information, metadata, and supporting content blocks in one screen.",
            commonWorkflows:
                "Use this page as a layout reference when changing how user profile data should be grouped or presented elsewhere in the dashboard.",
        }),
    },
    ...[
        ["/admin/buttons", "Buttons", "Review button variants, icon usage, and action affordances used throughout the admin interface."],
        ["/admin/grid-system", "Grid System", "Inspect layout grid behavior, responsive column combinations, and spacing conventions."],
        ["/admin/panels", "Panels", "Review panel and container patterns used for grouping related content."],
        ["/admin/sweet-alert", "SweetAlert", "Preview alert and confirmation patterns used for interruptive or high-importance feedback."],
        ["/admin/notifications", "Notifications", "Review inline and toast notification patterns, statuses, and message hierarchy."],
        ["/admin/icons", "Icons", "Inspect iconography usage and available icon treatments inside the design system."],
        ["/admin/typography", "Typography", "Review heading, body, caption, and utility text styles used across the UI."],
        ["/admin/regular-forms", "Regular Forms", "Preview standard form controls, labels, and basic data-entry layouts."],
        ["/admin/extended-forms", "Extended Forms", "Review richer form patterns such as advanced inputs, grouped fields, and composite controls."],
        ["/admin/validation-forms", "Validation Forms", "Inspect validation states, error messaging, and form submission feedback patterns."],
        ["/admin/wizard", "Wizard", "Review multi-step workflow patterns for guided setup or data collection."],
        ["/admin/regular-tables", "Regular Tables", "Preview standard table layouts, row density, and read-only tabular presentation."],
        ["/admin/extended-tables", "Extended Tables", "Review enhanced table patterns such as actions, filters, and richer row content."],
        ["/admin/react-tables", "React Tables", "Inspect the reusable React table implementation and its interactive features."],
        ["/admin/google-maps", "Google Maps", "Preview Google Maps integrations and location-based UI behavior."],
        ["/admin/full-screen-maps", "Full Screen Maps", "Review full-screen map rendering and layout behavior for spatial workflows."],
        ["/admin/vector-maps", "Vector Maps", "Inspect vector map rendering for choropleths, regional highlighting, and geographic summaries."],
        ["/admin/widgets", "Widgets", "Review dashboard widget patterns and compact information modules."],
        ["/admin/charts", "Charts", "Inspect chart components used for operational and financial reporting."],
        ["/admin/calendar", "Calendar", "Review scheduling and date-driven interaction patterns in calendar form."],
    ].map(([path, title, purpose]) => ({
        path,
        exact: false,
        help: createStandardHelp({
            title,
            description: `Reference page for the ${title.toLowerCase()} pattern library and related UI behavior.`,
            purpose,
            commonWorkflows:
                "Use this screen as a reference when validating visual behavior, interaction patterns, or layout decisions before applying them to production-facing modules.",
            bestPractices:
                "Treat this page as a design-system reference, not as a source of business data. Reuse established patterns from here instead of inventing one-off variants in production screens.",
        }),
    })),
];

export const helpConfig = {
    // ---------------------------------------------------------
    // PAGE-LEVEL HELP
    // ---------------------------------------------------------
    pages: [
        {
            path: "/admin/dashboard",
            exact: true,
            help: {
                title: "Platform Dashboard",
                description: "The main command center for your entire dealership operations.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            The Dashboard provides a high-level, real-time snapshot of your platform's health, sales performance, and immediate actionable items. It aggregates data across all your storefronts into unified metrics.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>Revenue Charts:</b> Visualize your daily, weekly, and monthly sales volume.</li>
                                <li><b>Pending Orders:</b> A quick access list of orders that require immediate fulfillment or attention.</li>
                                <li><b>System Alerts:</b> Notifications regarding sync failures, payment gateway issues, or low inventory warnings.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            Most users start their day here. Review your Pending Orders, check if Revenue targets are on track, and address any active System Alerts before navigating to specific management pages.
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            Data shown in the charts is cached for 15 minutes. Large spikes in traffic might take a few moments to reflect in the UI.
                        </HelpSection>
                        <HelpSection title="Best Practices">
                            Clicking on any bar in the Revenue Chart will directly take you to the detailed Sales Report for that specific timeframe. Check alerts daily.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/orders",
            exact: false,
            help: {
                title: "Order Management",
                description: "Review, process, and track all customer transactions across your storefronts.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            This is your central fulfillment hub. Every order placed by a customer lands here, allowing your fulfillment team to process payments, print shipping labels, and manage returns (RMAs).
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>Order List:</b> Shows Order ID, Customer, Date, Total, and current Status.</li>
                                <li><b>Filters:</b> Filter by Date Range, Status (Pending, Shipped, Cancelled), and Storefront.</li>
                                <li><b>Status:</b> Pending (unpaid), Processing (paid, packing), Shipped (tracking added), On Hold (fraud review).</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            Fulfillment teams will filter by "Processing", pick the items, print the packing slip, generate a shipping label, add the tracking number, and mark the order as "Shipped".
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            Always capture payment <i>before</i> changing an order status to "Shipped". If an order is flagged for high fraud risk, contact the customer to verify details before capturing the funds to avoid chargeback fees.
                        </HelpSection>
                        <HelpSection title="Best Practices">
                            Use bulk actions to update statuses for multiple orders simultaneously if using a 3PL integration.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/customers",
            exact: false,
            help: {
                title: "Customer Directory",
                description: "Manage your CRM data, assign price levels, and oversee customer accounts.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            The Customer Directory houses all registered accounts and guest checkout records. It allows you to segment your audience, view purchase histories, and manage B2B account permissions.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>Price Levels:</b> Tag customers to pricing tiers that override retail prices.</li>
                                <li><b>Tax Exemptions:</b> Upload Tax ID certificates to grant tax-exempt status.</li>
                                <li><b>Order History:</b> View lifetime value and past purchases.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            When an automotive repair shop creates an account, navigate here, verify their details, and update their "Price Level" to your Wholesale Tier so they see discounted pricing.
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            Deleting a customer does not delete their past financial transactions, which are retained for auditing purposes. 
                        </HelpSection>
                        <HelpSection title="Real-World Example">
                            Use the 'Login As' button to view the storefront exactly as a complaining customer sees it to troubleshoot pricing issues.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/customer-quotes",
            exact: false,
            help: {
                title: "Customer Quotes",
                description: "Manage and convert custom price quotes for B2B or specialized orders.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Manage quotes requested by customers. Allow sales reps to adjust pricing on a specific cart and send it back to the customer for approval and checkout.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>Quote Status:</b> Draft, Sent, Accepted, Expired, or Converted to Order.</li>
                                <li><b>Expiration Date:</b> The date after which the quoted prices are no longer valid.</li>
                                <li><b>Line Item Discounts:</b> Apply specific dollar or percentage discounts per item.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            A customer calls requesting a bulk discount. The sales rep creates a Quote, applies a 10% manual override, and emails the quote link. The customer clicks the link to pay.
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            Once a quote is Accepted and Converted, it cannot be edited. You must create a new order or modify the resulting order directly.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/catalogs",
            exact: false,
            help: {
                title: "Catalogs Management",
                description: "Manage global product catalogs and brand integrations.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Import, update, and structure entire manufacturer catalogs. This is the master database that feeds your individual store inventories.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>Catalog Sync:</b> Connect to API feeds from major distributors.</li>
                                <li><b>Brand Mapping:</b> Map incoming manufacturer names to your internal brand names.</li>
                                <li><b>Category Trees:</b> Define the global taxonomy (e.g., Engine -> Pistons).</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            When a new brand is onboarded, upload their CSV catalog here, map the columns (SKU, Price, Weight), and assign it to a category tree.
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            Changes here affect ALL dealers and storefronts. Do not delete a catalog unless you intend to wipe those products globally.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/parts",
            exact: false,
            help: {
                title: "Master Parts List",
                description: "View and edit the global database of all individual parts and SKUs.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            A granular view of every single product (part) in the system. Used to fix specific data errors, update master weights, or add universal fitment data.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>Part Number / SKU:</b> The universal identifier.</li>
                                <li><b>Supersessions:</b> Link an old discontinued part number to a new active part number.</li>
                                <li><b>Fitment Data:</b> Year/Make/Model applicability.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            A manufacturer replaces Part A with Part B. Navigate here, search for Part A, add Part B as the supersession, so customers searching for A automatically buy B.
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            Editing the base Cost here will immediately trigger pricing matrix recalculations for all dealers using this part.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/users",
            exact: false,
            help: {
                title: "Global User Management",
                description: "Manage administrative users and their platform-wide access.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Control who has access to the admin dashboard. This is for internal employee management, not customer management.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>User Email & Status:</b> Active, Suspended, or Pending Invite.</li>
                                <li><b>Assigned Roles:</b> Determines what menus and actions the user can see.</li>
                                <li><b>Store Restrictions:</b> Limit a user to only see orders/data for specific storefronts.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            When a new fulfillment employee is hired, click "Add User", enter their email, assign the "Fulfillment" role, and restrict them to the "Main Warehouse" store.
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            Never share accounts. Always create individual accounts for auditing purposes. Suspend users immediately upon termination.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/roles",
            exact: false,
            help: {
                title: "Role Permissions",
                description: "Define granular access control lists for different types of users.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Create custom roles (e.g., "Junior Support", "Marketing Manager") by toggling specific read/write permissions for every module in the application.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>Module Toggles:</b> Allow View, Edit, Create, or Delete for Orders, Products, Settings, etc.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            Create a "Read Only" role that allows viewing orders and customers but prevents any edits or exports, useful for temporary auditors.
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            The "Super Admin" role cannot be edited or deleted. Be extremely careful when granting "Export" permissions to prevent data theft.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealer-settings",
            exact: false,
            help: {
                title: "General Dealer Settings",
                description: "Configure your dealership's core operational parameters and branding.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            This is the foundational setup for your storefront. The information here feeds directly into your website's header, footer, contact pages, and transactional emails.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>Store Name & Logos:</b> Upload your primary and dark-mode logos. Ensure they are PNGs with transparent backgrounds.</li>
                                <li><b>Support Email & Phone:</b> This data is dynamically injected into Order Confirmation emails. Ensure it is accurate.</li>
                                <li><b>Timezone:</b> Crucial for accurately timestamping orders and generating end-of-day sales reports.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            When launching a new store or rebranding, update the logos, address, and support phone number here first.
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            Changing the Support Email requires re-verification if you are using our managed email delivery service to prevent spoofing.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealer-pricing",
            exact: false,
            help: {
                title: "Price Levels & Matrices",
                description: "Define global pricing rules, markups, and discount tiers.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            The Price Levels page allows you to create dynamic pricing strategies. Instead of manually pricing 10,000 parts, you create rules (e.g., "Cost + 20%") that automatically calculate retail and wholesale prices.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>Base Cost:</b> The manufacturer's cost to you.</li>
                                <li><b>MSRP:</b> The manufacturer's suggested retail price.</li>
                                <li><b>Matrix Rule:</b> A formula (e.g., Cost + X%, or MSRP - Y%) applied across a category or entire catalog.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            Create a "Retail" level set to MSRP - 5% to remain competitive online. Then, create a "Wholesale" level set to Cost + 15% and assign it exclusively to your B2B customers in the Customer Directory.
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            Pricing matrices are processed in a queue. If you apply a rule to 500,000 products, it may take up to 10 minutes for prices to update on the frontend.
                        </HelpSection>
                        <HelpSection title="Best Practices">
                            Always test your pricing matrices on a few high-value and low-value items before activating the rule globally to ensure margins remain healthy.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/store-policies",
            exact: false,
            help: {
                title: "Store Policies",
                description: "Set up and manage your store's return, shipping, and privacy policies.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Draft the legal and operational agreements that customers must accept before checkout. These are critical for fighting chargebacks.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>Return Policy:</b> Outline RMA windows, restocking fees, and non-returnable items (e.g., electrical parts).</li>
                                <li><b>Shipping Policy:</b> Cut-off times, carrier liabilities, and international shipping rules.</li>
                                <li><b>Privacy Policy:</b> Data retention and sharing agreements.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            Use the built-in rich text editor to paste your legal team's approved policies.
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            If you do not have a clear Return Policy, payment processors (like Stripe) will almost always side with the customer during a dispute.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealer-shipping",
            exact: false,
            help: {
                title: "Shipping Options",
                description: "Configure shipping carriers, live rates, and custom shipping rules.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Determine how much customers are charged for freight. Connect to carrier APIs (FedEx, UPS, USPS) for live rates, or set up flat-rate and free shipping thresholds.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>Live Carriers:</b> Enter your UPS/FedEx account credentials to pass negotiated rates to the customer.</li>
                                <li><b>Handling Fees:</b> Add a fixed $ amount or % markup to cover packaging materials.</li>
                                <li><b>Free Shipping Threshold:</b> Automatically offer free shipping if the cart exceeds a specific dollar value.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            Enable "Free Shipping over $150" but add an exclusion rule for "Oversized/Core Parts" to prevent losing money on heavy shipments.
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            Live rates will fail if products in the cart do not have accurate Weight and Dimensions configured in the Catalog.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/reports",
            exact: false,
            help: {
                title: "Reporting & Analytics",
                description: "Generate detailed financial, tax, and inventory reports.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Extract business intelligence. Run reports to reconcile accounting, pay sales taxes, or identify top-selling products.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>Sales Summary:</b> Gross revenue, discounts, taxes collected, and net revenue by day/month.</li>
                                <li><b>Tax Liability:</b> Breakdowns of tax collected per state/jurisdiction for remittance.</li>
                                <li><b>Abandoned Carts:</b> Metrics on how much revenue was lost at checkout.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            At the end of the month, the accounting team runs the Tax Liability report to file state sales taxes.
                        </HelpSection>
                        <HelpSection title="Best Practices">
                            Schedule important reports to be emailed to stakeholders automatically every Monday morning.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/rma",
            exact: false,
            help: {
                title: "RMA & Returns",
                description: "Manage Return Merchandise Authorizations and customer refunds.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Process requests from customers who want to return items. Track the receipt of the physical item back to the warehouse before issuing a refund.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>RMA Status:</b> Requested, Authorized, Received, Refunded, or Denied.</li>
                                <li><b>Restocking Fee:</b> Automatically calculate and deduct a percentage from the final refund amount.</li>
                                <li><b>Return Labels:</b> Generate and email a prepaid FedEx/UPS label to the customer.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            Review an RMA Request. If approved, change status to "Authorized" to email instructions. Once the warehouse marks it "Received", click "Process Refund".
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            Refunds cannot exceed the original capture amount. If you generate a prepaid return label, the cost of the label can be optionally deducted from their refund.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/markerplace-exporter",
            exact: false,
            help: {
                title: "Marketplace Exporter",
                description: "Push your product catalog to external sales channels like Amazon, eBay, or Google Shopping.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Expand your reach by exporting formatted CSVs or connecting API feeds to third-party marketplaces.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>Feed Templates:</b> Pre-configured column mappings that comply with Google Merchant Center or Amazon requirements.</li>
                                <li><b>Filter Rules:</b> Exclude products under $10 or out-of-stock items from being exported.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            Set up a daily recurring export to Google Shopping that includes only products with images and active inventory to maximize ad spend efficiency.
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            Marketplaces strictly require valid UPCs or MPNs. Products missing this data will be automatically excluded from the export to prevent account suspension.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/activity-log",
            exact: false,
            help: {
                title: "System Activity Log",
                description: "An immutable audit trail of all actions performed by admin users.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Track "Who did what, and when." Essential for security audits and troubleshooting unauthorized changes.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>Event Type:</b> e.g., "Order Edited", "Price Changed", "User Logged In".</li>
                                <li><b>User / IP Address:</b> Identifies the exact account and location that performed the action.</li>
                                <li><b>Before/After Delta:</b> Shows exactly what value was modified.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            If a product's price is suddenly too low, search the product SKU here to see which employee updated the pricing matrix incorrectly.
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            Logs cannot be deleted or modified by any user, including Super Admins, to ensure compliance.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/request",
            exact: false,
            help: {
                title: "Client Request",
                description: "Manage incoming client requests and custom inquiries.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Review and respond to specific requests submitted by clients. This serves as a ticketing queue for special requests outside standard orders.
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            Assign requests to sales reps, reply to inquiries, and convert valid requests into custom orders or quotes.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/new-message",
            exact: false,
            help: {
                title: "New Message",
                description: "Compose and send outbound messages to customers or internal staff.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Compose one-off outbound communication to selected recipients without going through the order flow. This page is typically used for operational updates, targeted outreach, or manual follow-up with a known customer list.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>To:</b> Opens the recipient selector so you can search and pick the exact customers who should receive the message.</li>
                                <li><b>Subject:</b> The email subject or primary message label seen by the recipient.</li>
                                <li><b>Message:</b> Rich-text body where you compose or adapt the outbound content.</li>
                                <li><b>Attachment:</b> Optional file upload for PDFs, images, or supporting documents.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            Select recipients, define a clear subject, compose the body, attach any supporting file, and then send the message once the audience and content are verified.
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            This action is outbound and customer-facing. A wrong recipient list or stale attachment will be sent exactly as selected, so verify the audience before submitting.
                        </HelpSection>
                        <HelpSection title="Best Practices">
                            Keep subjects explicit, keep the body short, and use approved template language when possible so branding and tone remain consistent across stores.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/orders-v2",
            exact: false,
            help: {
                title: "Orders V2 (Beta)",
                description: "The next-generation order fulfillment interface.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            A modernized version of the Order Management screen offering faster bulk processing, improved filtering, and enhanced UX.
                        </HelpSection>
                        <HelpSection title="Business Rules">
                            Orders V2 uses the exact same database as the legacy Orders screen. Changes here reflect globally.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealer-roles",
            exact: false,
            help: {
                title: "Dealer Roles",
                description: "Manage roles specifically scoped to individual dealerships.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            While global roles affect the entire platform, Dealer Roles allow you to create restricted roles for specific storefronts (e.g., "Store A Manager").
                        </HelpSection>
                        <HelpSection title="Best Practices">
                            Ensure a user is assigned to the correct store before applying a Dealer Role to prevent accidental cross-store data leakage.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/payment-methods",
            exact: false,
            help: {
                title: "Payment Methods",
                description: "Configure accepted payment gateways and offline methods.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Enable or disable Stripe, PayPal, Authorize.net, or offline methods like "Purchase Order" and "Wire Transfer".
                        </HelpSection>
                        <HelpSection title="Business Rules">
                            Disabling a gateway will immediately prevent customers from using it at checkout. Ensure at least one primary gateway is always active.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealers",
            exact: false,
            help: {
                title: "Dealers Directory",
                description: "Manage all multi-tenant dealerships within the platform.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            View the list of all active storefronts/dealers. As a Super Admin, you can provision new dealers, suspend accounts, and monitor dealer health.
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            Click 'Impersonate' to log in as a specific dealer and troubleshoot their settings directly.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/translate",
            exact: false,
            help: {
                title: "Translate Manager",
                description: "Manage platform-wide string translations and localization.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Modify hardcoded UI text across the frontend. You can translate strings into Spanish, French, or simply change wording (e.g., "Cart" to "Bag").
                        </HelpSection>
                        <HelpSection title="Business Rules">
                            Translations are cached. It may take a hard refresh (Ctrl+F5) for changes to appear on the live site.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/managed-dns-zones",
            exact: false,
            help: {
                title: "Managed DNS Zones",
                description: "Configure domain name system records for storefronts.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Manage A, CNAME, and TXT records for custom domains directly within the platform instead of relying on external registrars.
                        </HelpSection>
                        <HelpSection title="Warnings">
                            Incorrect DNS changes will instantly take down the storefront. Only edit these if you are an IT administrator.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/managed-robots-txt",
            exact: false,
            help: {
                title: "Managed Robots.txt",
                description: "Control search engine crawling directives.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Edit the robots.txt file to instruct Googlebot and Bing on which pages they should or shouldn't index (e.g., blocking the cart/checkout URLs).
                        </HelpSection>
                        <HelpSection title="Warnings">
                            A single typo here (like Disallow: /) can completely de-index your entire website from Google. Proceed with extreme caution.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/homepage-categories",
            exact: false,
            help: {
                title: "Homepage Categories",
                description: "Select which catalog categories are featured on the homepage.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Merchandising tool to highlight specific categories (e.g., "Brakes", "Suspension") visually on the storefront's main page.
                        </HelpSection>
                        <HelpSection title="Best Practices">
                            Rotate these seasonally. For example, feature "Wiper Blades" in winter and "A/C Parts" in summer.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealer-profile",
            exact: false,
            help: {
                title: "Dealer Profile",
                description: "View and edit your personal dealership account information.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Update your primary contact name, billing address, and account-level preferences.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/general-settings",
            exact: false,
            help: {
                title: "General Settings",
                description: "Configure core platform parameters.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Set global variables like currency, default weight units (LBS vs KG), and primary timezone.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/footer-settings",
            exact: false,
            help: {
                title: "Footer Options",
                description: "Customize the bottom section of your storefront.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Add social media links, quick navigation columns, and copyright text to the website footer.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealer-redirects",
            exact: false,
            help: {
                title: "URL Redirects",
                description: "Manage 301 and 302 redirects for SEO preservation.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            If you delete an old product or change a page URL, create a 301 redirect here to point the old link to the new link, preserving Google rank and preventing 404 errors.
                        </HelpSection>
                        <HelpSection title="Best Practices">
                            Always test your redirects after creating them to ensure you didn't accidentally create an infinite redirect loop.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealer-scripts",
            exact: false,
            help: {
                title: "Dealer Scripts",
                description: "Inject custom JavaScript and tracking pixels.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Add Google Analytics (GA4), Facebook Pixel, or live chat widgets (e.g., Intercom) by pasting their `&lt;script&gt;` tags into the `&lt;head&gt;` or `&lt;body&gt;`.
                        </HelpSection>
                        <HelpSection title="Warnings">
                            Malicious or broken JavaScript can break the checkout flow. Always verify scripts.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealer-catalogs",
            exact: false,
            help: {
                title: "Dealer Catalogs",
                description: "Manage which specific brand catalogs are active for this store.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            You may have 50 global catalogs, but this store only sells 10 brands. Toggle which catalogs are visible and purchasable on this specific storefront.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealer-payments",
            exact: false,
            help: {
                title: "Dealer Payments",
                description: "Review your billing and subscription payments to the platform.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            This is your internal billing portal where you pay your monthly SaaS fees, download invoices, and update your credit card on file.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/manage-shipping-options",
            exact: false,
            help: {
                title: "Manage Shipping Options",
                description: "Configure universal shipping rules and carrier integrations.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Define the logistical rules for fulfillment, including box dimensions, carrier account keys, and handling time estimates.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealer-pagetech",
            exact: false,
            help: {
                title: "Page Settings",
                description: "Advanced technical configuration for page rendering.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Adjust caching rules, lazy loading preferences, and CDN settings to optimize storefront speed.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealer-users",
            exact: false,
            help: {
                title: "Dealer Users",
                description: "Manage employee access specifically for this dealership.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Invite staff members, mechanics, or fulfillment workers to access this store's dashboard without granting them global platform access.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/google-shopping-feed",
            exact: false,
            help: {
                title: "Google Shopping Feed",
                description: "Configure your direct integration with Google Merchant Center.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Automatically sync your active inventory, prices, and images directly to Google Shopping to run Product Listing Ads (PLAs).
                        </HelpSection>
                        <HelpSection title="Business Rules">
                            Google strictly requires GTINs (UPCs) and Shipping details. Products missing these will be rejected by Google.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealer-tax",
            exact: false,
            help: {
                title: "Sales Tax Configuration",
                description: "Manage state-by-state tax nexus and collection rates.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Define which states you have physical or economic nexus in. The system will automatically calculate and collect appropriate sales tax at checkout.
                        </HelpSection>
                        <HelpSection title="Best Practices">
                            If you hit the economic threshold ($100k or 200 transactions) in a new state, you must enable tax collection here immediately to remain compliant.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/reports/sales-report",
            exact: false,
            help: {
                title: "Sales Reports",
                description: "Detailed analytics on revenue, units sold, and profit margins.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Deep dive into your financial data. Export CSVs of daily transactions, filter by brand, or analyze profit margins based on cost data.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealer-pages",
            exact: false,
            help: {
                title: "Custom Pages",
                description: "Create standard informational pages for your website.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Use the WYSIWYG editor to create an 'FAQ', 'Contact Us', or 'Installation Guides' page. These pages can then be linked in the header or footer.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/site-style",
            exact: false,
            help: {
                title: "Site Style",
                description: "Theme color customizer and typography settings.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Change your storefront's primary button colors, background themes, and font families to match your brand guidelines without writing CSS.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/about-us",
            exact: false,
            help: {
                title: "About Us Page",
                description: "Edit the narrative content for your company's About page.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            A dedicated editor specifically for the 'About Us' section, allowing you to upload team photos and company history to build trust with buyers.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/home-page-specials",
            exact: false,
            help: {
                title: "Home Page Specials",
                description: "Feature discounted or promotional items on the main landing page.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Select specific SKUs to render in a 'Special Offers' carousel on the homepage to drive immediate conversions.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/popular-models",
            exact: false,
            help: {
                title: "Popular Models",
                description: "Highlight specific vehicle models for easy navigation.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Allow customers to quickly filter parts by clicking on popular vehicles (e.g., 'Ford F-150', 'Honda Civic') directly from the homepage.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/message-template",
            exact: false,
            help: {
                title: "Message Templates",
                description: "Pre-written email and SMS responses.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Draft canned responses for common support queries (e.g., 'Where is my order?', 'RMA Instructions') to drastically speed up customer service.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/pick-up-options",
            exact: false,
            help: {
                title: "Pick Up Options",
                description: "Configure 'Buy Online, Pick Up In Store' (BOPIS) rules.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Enable local pickup at checkout. Set handling times (e.g., 'Ready in 2 hours') and provide instructions on where customers should park when they arrive.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/bulk-items-configuration",
            exact: false,
            help: {
                title: "Bulk Edit Item",
                description: "Mass update products using spreadsheets or batch actions.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Instead of opening 500 parts individually, use this grid to quickly apply mass price changes, assign categories, or update shipping weights.
                        </HelpSection>
                        <HelpSection title="Warnings">
                            Bulk edits cannot be undone. Always export a backup CSV before running a mass overwrite.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/manage-related-items",
            exact: false,
            help: {
                title: "Manage Related Items",
                description: "Configure cross-sells and upsells.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Link items together (e.g., linking Brake Pads to Brake Rotors) so they appear as 'Frequently Bought Together' suggestions on the product page.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/message-center",
            exact: false,
            help: {
                title: "Message Center",
                description: "Inbox for all customer communications.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            View and reply to 'Contact Us' submissions, order inquiries, and internal staff notes in a threaded view.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/edit-item",
            exact: false,
            help: {
                title: "Edit Item",
                description: "Modify generic non-automotive products or merchandise.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Manage apparel, accessories, or generic items that don't rely on Year/Make/Model fitment data.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/edit-part",
            exact: false,
            help: {
                title: "Edit Part",
                description: "Modify specific automotive part fitment and data.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            A highly specialized editor for automotive parts, containing fields for VIN validation, supersessions, and core charges.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/image-library",
            exact: false,
            help: {
                title: "Image Library",
                description: "Centralized media management.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Upload, crop, and manage all product photos, banners, and logos. The library automatically compresses images for web performance.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/part-number-url",
            exact: false,
            help: {
                title: "Part Number URL",
                description: "Configure URL structures for SEO.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Determine how product links are generated. For example, choose whether a product link should include the Brand name (`/brand/part`) or just the part number (`/p/part-number`).
                        </HelpSection>
                        <HelpSection title="Warnings">
                            Changing URL structures will cause massive 404 errors on Google. Only do this if you understand the SEO implications and have proper 301 redirects set up.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealer-coupons",
            exact: false,
            help: {
                title: "Dealer Coupons",
                description: "Create promo codes and discount offers.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Generate codes like 'SAVE10' for 10% off. You can restrict coupons by category, minimum cart value, or expiration date.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/store-manager",
            exact: false,
            help: {
                title: "Store Manager",
                description: "High-level management of all child stores.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Used by franchise owners to oversee multiple locations, sync settings across stores, and view aggregate revenue.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/edit-store",
            exact: false,
            help: {
                title: "Edit Store",
                description: "Modify storefront configurations.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Edit the specific domain routing, active status, and master settings for an individual storefront instance.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/manage-subscriptions",
            exact: false,
            help: {
                title: "Manage Subscriptions",
                description: "Handle recurring customer orders or platform billing.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Create and maintain the subscription plans used to price dealer/store tiers. This screen centralizes the plan catalog that downstream workflows use when assigning or reviewing store subscriptions.
                        </HelpSection>
                        <HelpSection title="Major Sections & Fields">
                            <ul>
                                <li><b>Subscription Name:</b> The internal and display label for the plan.</li>
                                <li><b>Plan Description:</b> Short explanation of what the subscription includes.</li>
                                <li><b>Setup Fee / Monthly Fee / Cancel Fee:</b> Commercial terms applied to the plan.</li>
                                <li><b>Active:</b> Determines whether the plan can be used for new assignments.</li>
                            </ul>
                        </HelpSection>
                        <HelpSection title="Common Workflows">
                            Create a new plan when commercial terms change, edit pricing on an existing plan, and deactivate legacy plans that should no longer be assigned to new stores.
                        </HelpSection>
                        <HelpSection title="Business Rules & Warnings">
                            Changing a plan definition affects how operators interpret and assign that plan going forward. Deactivate obsolete plans instead of silently reusing the same name for different commercial terms.
                        </HelpSection>
                        <HelpSection title="Best Practices">
                            Use stable naming, keep fee fields explicit, and avoid ambiguous descriptions so finance and operations teams can tell plans apart quickly.
                        </HelpSection>
                    </>
                )
            }
        },
        {
            path: "/admin/dealer-inventory-upload",
            exact: false,
            help: {
                title: "Dealer Inventory Upload",
                description: "Sync local inventory quantities.",
                content: (
                    <>
                        <HelpSection title="Purpose of the screen">
                            Upload a CSV from your local DMS (Dealer Management System) to update your active 'Quantity on Hand' across the website to prevent out-of-stock sales.
                        </HelpSection>
                        <HelpSection title="Best Practices">
                            If you don't have an API integration, make it a daily routine to upload your inventory CSV at the end of every business day.
                        </HelpSection>
                    </>
                )
            }
        },
        ...additionalRouteHelps
    ],

    // ---------------------------------------------------------
    // MODAL-LEVEL HELP (Detected automatically via MutationObserver)
    // Keys match the text content of the .MuiDialogTitle-root element.
    // ---------------------------------------------------------
    modals: {
        "Edit Product": {
            matchTitle: "Edit Product", 
            title: "Edit Product Configuration",
            description: "Modify individual item details, override pricing, and adjust SEO settings.",
            content: (
                <>
                    <HelpSection title="What is this modal for?">
                        This modal allows you to fine-tune an individual product. Changes made here will instantly reflect on the customer-facing storefront and override global catalog settings.
                    </HelpSection>
                    <HelpSection title="Critical Fields">
                        <ul>
                            <li><b>SKU / Part Number:</b> The unique identifier. Do not change this unless absolutely necessary, as it may break inventory syncs.</li>
                            <li><b>Weight & Dimensions:</b> Crucial for accurate real-time shipping rate calculations (FedEx/UPS). Ensure these are exact.</li>
                            <li><b>Override Price:</b> If filled, this ignores your global Pricing Matrices and locks the product at this specific price.</li>
                            <li><b>Status:</b> Set to Draft to hide the product from the public while you prepare images.</li>
                        </ul>
                    </HelpSection>
                    <HelpSection title="Business Rules & Warnings">
                        Modifying the Weight here will override the manufacturer's provided weight. If the item is marked as "Oversized", it may bypass Free Shipping thresholds based on your Shipping Settings.
                    </HelpSection>
                    <HelpSection title="SEO Optimization Tips">
                        The SEO tab allows you to write custom Meta Titles and Descriptions. This is highly recommended for your top 50 best-selling products to improve Google Search rankings. Don't stuff keywords; write for humans!
                    </HelpSection>
                </>
            )
        },
        "Add New User": {
            matchTitle: "Add New User",
            title: "User Provisioning",
            description: "Create an administrative account and assign permissions.",
            content: (
                <>
                    <HelpSection title="What is this modal for?">
                        Provision access for your employees. You must assign them a Role to dictate what they can and cannot see in the Admin dashboard.
                    </HelpSection>
                    <HelpSection title="Understanding Roles">
                        <ul>
                            <li><b>Super Admin:</b> Unrestricted access to all settings, billing, and user management.</li>
                            <li><b>Sales / Fulfillment:</b> Can view and process Orders and Customers, but cannot modify Pricing Matrices.</li>
                            <li><b>Catalog Manager:</b> Can edit Products and Categories, but cannot view financial reports.</li>
                        </ul>
                    </HelpSection>
                    <HelpSection title="Security Best Practice">
                        Enforce strong passwords and always grant the principle of <i>least privilege</i>. Only assign the Super Admin role to business owners or IT managers. Suspend accounts immediately when employees leave.
                    </HelpSection>
                </>
            )
        },
        "Process Refund": {
            matchTitle: "Process Refund",
            title: "Process Order Refund",
            description: "Return funds to the customer's original payment method.",
            content: (
                <>
                    <HelpSection title="What is this modal for?">
                        Execute a partial or full refund through your payment gateway (e.g., Stripe, PayPal) directly from the dashboard.
                    </HelpSection>
                    <HelpSection title="Critical Fields">
                        <ul>
                            <li><b>Refund Amount:</b> You can specify an exact dollar amount for partial refunds (e.g., to withhold original shipping costs).</li>
                            <li><b>Restocking Fee:</b> Automatically calculates a deduction based on your Store Policies.</li>
                            <li><b>Reason Code:</b> Required for accounting. e.g., "Customer Regret", "Damaged in Transit".</li>
                        </ul>
                    </HelpSection>
                    <HelpSection title="Business Rules & Warnings">
                        Refunds are irreversible. Once you click submit, the funds are immediately sent back to the customer's bank. It may take 3-5 business days for it to appear on their statement.
                    </HelpSection>
                </>
            )
        },
        "Capture Payment": {
            matchTitle: "Capture",
            title: "Capture Authorized Payment",
            description: "Finalize the transaction and move funds from the customer's hold to your bank.",
            content: (
                <>
                    <HelpSection title="What is this modal for?">
                        If your store is set to "Authorize Only", customer credit cards are only verified at checkout. You must use this modal to actually Capture the funds before the authorization expires (usually 7 days).
                    </HelpSection>
                    <HelpSection title="Common Workflows">
                        A customer buys a part that might be out of stock. You verify inventory first. If you have it, you Capture the full payment here. If you only have half the order, you can do a Partial Capture and release the rest.
                    </HelpSection>
                    <HelpSection title="Business Rules & Warnings">
                        You can never capture MORE than the originally authorized amount. If the order total increased, you must send them a new invoice.
                    </HelpSection>
                </>
            )
        },
        "Add Tracking": {
            matchTitle: "Add Tracking",
            title: "Add Shipment Tracking",
            description: "Attach tracking numbers and notify the customer.",
            content: (
                <>
                    <HelpSection title="What is this modal for?">
                        Manually input tracking details if you are not using an automated shipping integration. Submitting this will change the order status to Shipped and trigger the Shipping Confirmation email.
                    </HelpSection>
                    <HelpSection title="Critical Fields">
                        <ul>
                            <li><b>Carrier:</b> Select FedEx, UPS, USPS, etc. to ensure the tracking link in the email works correctly.</li>
                            <li><b>Tracking Number:</b> Ensure there are no spaces.</li>
                            <li><b>Notify Customer:</b> Keep this checked to automatically dispatch the email.</li>
                        </ul>
                    </HelpSection>
                    <HelpSection title="Best Practices">
                        If an order ships in multiple boxes, you can add multiple tracking numbers by repeating this process.
                    </HelpSection>
                </>
            )
        }
    }
};
